import React, { useContext, useEffect, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData from "../Datas/productData";
import { Link } from "react-router-dom";
import siteConfig, { checkoutCountries, formatPrice } from "../config/siteConfig";

const initialForm = {
  email: "",
  country: "Hong Kong",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  address2: "",
  city: "",
  postalCode: "",
};

const createOrderNumber = () => {
  const random = new Uint32Array(2);
  window.crypto.getRandomValues(random);
  return `${Date.now()}${random[0].toString().padStart(10, "0")}${random[1]
    .toString()
    .padStart(10, "0")}`;
};

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = `Checkout — ${siteConfig.brandName}`;
  }, []);

  const items = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = items.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const shipping = siteConfig.shipping.standard;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Enter a valid email address.";
    ["firstName", "lastName", "address", "city", "postalCode"].forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required.";
    });
    return nextErrors;
  };

  const submitOrder = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstField = Object.keys(nextErrors)[0];
      document.querySelector(`[name="${firstField}"]`)?.focus();
      return;
    }

    setIsSubmitting(true);
    const orderNumber = createOrderNumber();
    const successUrl = new URL("/payment", window.location.origin);
    successUrl.searchParams.set("state", "success");
    const cancelUrl = new URL("/payment", window.location.origin);
    cancelUrl.searchParams.set("state", "cancel");
    const paymentUrl = new URL("/create_payment.php", window.location.origin);
    paymentUrl.search = new URLSearchParams({
      orderNo: orderNumber,
      amount: total.toFixed(2),
      currency: siteConfig.currency.code,
      email: form.email.trim(),
      success_url: successUrl.toString(),
      cancel_url: cancelUrl.toString(),
      title: items[0].product.name,
    }).toString();

    window.sessionStorage.setItem(
      "yorthmarketco-pending-order",
      JSON.stringify({ orderNumber, total, email: form.email.trim() })
    );
    window.location.assign(paymentUrl.toString());
  };

  if (items.length === 0) {
    return (
      <main className="checkout-empty">
        <p>Checkout</p>
        <h1>Your bag is empty</h1>
        <Link to="/shop">Return to the collection</Link>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <form className="checkout-form" onSubmit={submitOrder} noValidate>
        <section className="checkout-form__section">
          <div className="checkout-section-title">
            <span>01</span>
            <h1>Contact</h1>
          </div>
          <label className="checkout-field checkout-field--full">
            <span>Email address</span>
            <input name="email" type="email" autoComplete="email" value={form.email} onChange={updateField} aria-invalid={Boolean(errors.email)} />
            {errors.email && <small>{errors.email}</small>}
          </label>
        </section>

        <section className="checkout-form__section">
          <div className="checkout-section-title">
            <span>02</span>
            <h2>Delivery</h2>
          </div>
          <div className="checkout-fields">
            <label className="checkout-field checkout-field--full">
              <span>Country / region</span>
              <select name="country" value={form.country} onChange={updateField}>
                {checkoutCountries.map((country) => <option key={country}>{country}</option>)}
              </select>
            </label>
            <label className="checkout-field">
              <span>First name</span>
              <input name="firstName" autoComplete="given-name" value={form.firstName} onChange={updateField} aria-invalid={Boolean(errors.firstName)} />
              {errors.firstName && <small>{errors.firstName}</small>}
            </label>
            <label className="checkout-field">
              <span>Last name</span>
              <input name="lastName" autoComplete="family-name" value={form.lastName} onChange={updateField} aria-invalid={Boolean(errors.lastName)} />
              {errors.lastName && <small>{errors.lastName}</small>}
            </label>
            <label className="checkout-field checkout-field--full">
              <span>Company (optional)</span>
              <input name="company" autoComplete="organization" value={form.company} onChange={updateField} />
            </label>
            <label className="checkout-field checkout-field--full">
              <span>Address</span>
              <input name="address" autoComplete="street-address" value={form.address} onChange={updateField} aria-invalid={Boolean(errors.address)} />
              {errors.address && <small>{errors.address}</small>}
            </label>
            <label className="checkout-field checkout-field--full">
              <span>Apartment, suite, etc. (optional)</span>
              <input name="address2" value={form.address2} onChange={updateField} />
            </label>
            <label className="checkout-field">
              <span>City</span>
              <input name="city" autoComplete="address-level2" value={form.city} onChange={updateField} aria-invalid={Boolean(errors.city)} />
              {errors.city && <small>{errors.city}</small>}
            </label>
            <label className="checkout-field">
              <span>Postal code</span>
              <input name="postalCode" autoComplete="postal-code" value={form.postalCode} onChange={updateField} aria-invalid={Boolean(errors.postalCode)} />
              {errors.postalCode && <small>{errors.postalCode}</small>}
            </label>
          </div>
          <div className="shipping-method">
            <span>Tracked standard delivery</span>
            <strong>{formatPrice(shipping)}</strong>
          </div>
        </section>

        <section className="checkout-form__section">
          <div className="checkout-section-title">
            <span>03</span>
            <h2>Payment</h2>
          </div>
          <p className="payment-note">After reviewing your details, Pay now opens our secure payment page. Card details are entered with the payment provider and are not collected by this form.</p>
          <div className="payment-card">
            <div className="payment-card__head">
              <span>Secure payment page</span>
              <div aria-label="Accepted payment methods"><i>VISA</i><i>MC</i><i>AMEX</i><i>UP</i></div>
            </div>
            <p className="payment-card__redirect">You will be redirected after selecting Pay now.</p>
          </div>
        </section>

        <button className="checkout-submit" type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Redirecting…" : `Pay now · ${formatPrice(total)}`}
        </button>
        <p className="checkout-legal">By placing this order, you agree to our <Link to="/terms">terms</Link> and <Link to="/privacy">privacy notice</Link>.</p>
      </form>

      <aside className="checkout-summary">
        <div className="checkout-summary__inner">
          <p className="checkout-summary__label">Order summary</p>
          <div className="checkout-summary__items">
            {items.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div>
                  <img src={item.product.image} alt={item.product.name} />
                  <span>{item.quantity}</span>
                </div>
                <p>
                  <strong>{item.product.name}</strong>
                  <small>{item.color} / {item.size}</small>
                </p>
                <b>{formatPrice(item.product.price * item.quantity)}</b>
              </article>
            ))}
          </div>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Shipping</dt><dd>{formatPrice(shipping)}</dd></div>
          </dl>
          <div className="checkout-summary__total"><span>Total <small>{siteConfig.currency.code}</small></span><strong>{formatPrice(total)}</strong></div>
        </div>
      </aside>
    </main>
  );
};

export default CheckOut;
